import React, { useEffect, useState } from "react";
import { ordersApi } from "../services/api/orderApi";
import { IOrder } from "../types";
import { showError, showSuccess } from "../lib/toast";
import ScrollToTop from "../components/ScrollToTop";
import ImageWithLoader from "../components/ImageWithLoader";

interface GroupedOrders {
  [date: string]: IOrder[];
}

const PreviousPurchase: React.FC = () => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null);
  const [downloadingImages, setDownloadingImages] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response: any = await ordersApi.getOrders({
        limit: 100
        // Remove status filter to get all orders, then filter on frontend
      });

      console.log('Orders API response:', response);

      if (response.success && response.data?.orders) {
        // Filter for completed orders on the frontend
        const completedOrders = response.data.orders.filter((order: IOrder) =>
          order.status === 'completed' && order.paymentStatus === 'paid'
        );
        console.log('Completed orders:', completedOrders);
        setOrders(completedOrders);
      } else {
        console.log('No orders found or API error:', response);
        setOrders([]);
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      showError("Failed to load previous purchases");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return `${day} / ${weekday}`;
  };

  const handleDownloadImages = async (orderId: string) => {
    try {
      setDownloadingImages(orderId);
      const response: any = await ordersApi.getOrderProductImages(orderId);

      if (response.success && response.data?.images) {
        let successCount = 0;
        let failCount = 0;

        // Download each image with proper authentication
        for (let i = 0; i < response.data.images.length; i++) {
          const imageUrl = response.data.images[i];

          try {
            // Handle different types of URLs
            let fullImageUrl = imageUrl;
            let needsAuth = false;

            if (imageUrl.startsWith('http')) {
              // External URL (like S3) - use proxy endpoint to avoid CORS issues
              const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4004';
              const token = localStorage.getItem('accessToken');

              try {
                const proxyUrl = `${baseURL}/api/v1/invoices/download-image/${orderId}/${i}`;
                const imageResponse = await fetch(proxyUrl, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });

                if (!imageResponse.ok) {
                  throw new Error(`HTTP ${imageResponse.status}`);
                }

                // Convert to blob
                const blob = await imageResponse.blob();

                // Determine file extension from content type or URL
                let extension = 'jpg';
                if (blob.type) {
                  if (blob.type.includes('png')) extension = 'png';
                  else if (blob.type.includes('gif')) extension = 'gif';
                  else if (blob.type.includes('webp')) extension = 'webp';
                } else if (imageUrl.toLowerCase().includes('.png')) {
                  extension = 'png';
                } else if (imageUrl.toLowerCase().includes('.gif')) {
                  extension = 'gif';
                }

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `product-${i + 1}.${extension}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                successCount++;
              } catch (proxyError) {
                console.warn(`Proxy download failed for image ${i + 1}, trying direct method:`, proxyError);

                // Fallback: Try direct download
                try {
                  const imageResponse = await fetch(imageUrl, {
                    method: 'GET',
                    mode: 'cors',
                  });

                  if (!imageResponse.ok) {
                    throw new Error(`HTTP ${imageResponse.status}`);
                  }

                  const blob = await imageResponse.blob();

                  let extension = 'jpg';
                  if (blob.type) {
                    if (blob.type.includes('png')) extension = 'png';
                    else if (blob.type.includes('gif')) extension = 'gif';
                    else if (blob.type.includes('webp')) extension = 'webp';
                  }

                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `product-${i + 1}.${extension}`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);

                  successCount++;
                } catch (directError) {
                  console.warn(`Direct download also failed for image ${i + 1}:`, directError);

                  // Final fallback: Open image in new tab for manual download
                  const newWindow = window.open(imageUrl, '_blank');
                  if (newWindow) {
                    successCount++;
                    showSuccess(`Image ${i + 1} opened in new tab for download`);
                  } else {
                    failCount++;
                  }
                }
              }
            } else {
              // Relative URL - make it absolute with auth
              const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
              fullImageUrl = `${baseURL}${imageUrl}`;
              needsAuth = true;

              // Try to fetch the image
              const fetchOptions: RequestInit = {
                method: 'GET',
                mode: 'cors',
              };

              if (needsAuth) {
                const token = localStorage.getItem('accessToken');
                if (token) {
                  fetchOptions.headers = {
                    'Authorization': `Bearer ${token}`,
                  };
                }
              }

              const imageResponse = await fetch(fullImageUrl, fetchOptions);

              if (!imageResponse.ok) {
                console.error(`Failed to fetch image ${i + 1}:`, imageResponse.statusText);
                failCount++;
                continue;
              }

              // Convert to blob
              const blob = await imageResponse.blob();

              // Determine file extension from content type or URL
              let extension = 'jpg';
              if (blob.type) {
                if (blob.type.includes('png')) extension = 'png';
                else if (blob.type.includes('gif')) extension = 'gif';
                else if (blob.type.includes('webp')) extension = 'webp';
              } else if (imageUrl.toLowerCase().includes('.png')) {
                extension = 'png';
              } else if (imageUrl.toLowerCase().includes('.gif')) {
                extension = 'gif';
              }

              // Create download link
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `product-${i + 1}.${extension}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              successCount++;
            }

            // Add small delay between downloads to avoid browser blocking
            if (i < response.data.images.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (imageError) {
            console.error(`Error downloading image ${i + 1}:`, imageError);
            failCount++;
            // Continue with next image even if one fails
          }
        }

        // Show appropriate success/error message
        if (successCount > 0 && failCount === 0) {
          showSuccess(`All ${successCount} product images downloaded successfully`);
        } else if (successCount > 0 && failCount > 0) {
          showSuccess(`${successCount} images processed successfully, ${failCount} failed`);
        } else {
          showError("Failed to download any product images. Please try again or contact support.");
        }
      } else {
        showError("No images found for this order");
      }
    } catch (error) {
      console.error("Failed to download images:", error);
      showError("Failed to download product images");
    } finally {
      setDownloadingImages(null);
    }
  };

  const handleDownloadInvoice = async (orderId: string, orderNumber: string) => {
    try {
      setDownloadingInvoice(orderId);
      const blob = await ordersApi.downloadInvoice(orderId);

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess("Invoice downloaded successfully");
    } catch (error) {
      console.error("Failed to download invoice:", error);
      showError("Failed to download invoice");
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Group orders by date
  const groupedOrders = orders.reduce((acc, order) => {
    const date = formatDate(order.createdAt);
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(order);
    return acc;
  }, {} as GroupedOrders);

  if (loading) {
    return (
      <div>
        <section className="previous_purchase_banner_section" id="previous_purchase_banner">
          <div className="common_banner_section" id="common_banner_sec">
            <div className="page_navegation_area">
              <div className="container">
                <div className="page_navegation font_two">
                  <p>
                    <a href="/">Home</a> /{" "}
                    <span className="active fw_700">Previous Purchase</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="previous_purchase_section">
          <div className="container">
            <div className="previous_purchase_area">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading your purchases...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <ScrollToTop />
      <section
        className="previous_purchase_banner_section"
        id="previous_purchase_banner"
      >
        <div className="common_banner_section" id="common_banner_sec">
          <div className="page_navegation_area">
            <div className="container">
              <div className="page_navegation font_two">
                <p>
                  <a href="/">Home</a> /{" "}
                  <span className="active fw_700">Previous Purchase</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Previous Purchase Content Section */}
      <section className="previous_purchase_section">
        <div className="container">
          <div className="previous_purchase_area">
            {/* Page Title */}
            <div className="previous_purchase_header">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h1 className="page_title font_one">Previous Purchase</h1>
                </div>
              </div>
            </div>

            {/* Purchase History */}
            <div className="purchase_history">
              {orders.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No previous purchases found.</p>
                  <a href="/products?collection=Canvas" className="btn btn-primary mt-3">
                    Start Shopping
                  </a>
                </div>
              ) : (
                Object.entries(groupedOrders).map(([date, orderList]) => (
                  <div key={date} className="purchase_date_group">
                    {/* Date Header */}
                    <div className="date_header">
                      <h3 className="date_title font_two">{date}</h3>
                    </div>

                    {/* Purchase Items */}
                    <div className="purchase_items">
                      {orderList.map((order) =>
                        order.items.map((item, itemIndex) => {
                          const imageUrl = item.product?.image || item.product?.subImages?.[0] || item.product?.images?.[0] || '/assets/images/product_grid_img_1.png';
                          return (
                            <div key={`${order.orderNumber}-${itemIndex}`}>
                              <div className="d-flex align-items-center justify-content-between order_with_date">
                                <p className="font_three order_with_date">
                                  Order ID: #{order.orderNumber}
                                </p>
                                <div className="order_with_date">
                                  <h3 className="date_title font_two">{date}</h3>
                                </div>
                              </div>
                              <div className="purchase_item">
                                <div className="purchase_item_content">

                                  <div className="purchase_image">
                                    <ImageWithLoader
                                      src={imageUrl}
                                      alt={item.name}
                                      fallbackSrc="/assets/images/product_grid_img_1.png"
                                    />
                                  </div>

                                  <div className="purchase_details">
                                    <p className="order_id font_three">
                                      Order ID: #{order.orderNumber}
                                    </p>
                                    <h4 className="product_name font_one">
                                      {item.name}
                                    </h4>

                                    <p className="product_price font_two">
                                      ${item.price.toFixed(2)}
                                    </p>

                                    {/* Dimensions */}
                                    {item.product?.fileDetails?.recommendedPrintSize && (
                                      <p className="product_dimensions font_two">
                                        <span>Dimensions:</span> <span className="fw_400">{item.product?.fileDetails?.recommendedPrintSize}</span>
                                      </p>
                                    )}
                                  </div>

                                  <div className="purchase_actions purchase_action_max">
                                    <button
                                      className="purchase_download_btn"
                                      onClick={() => handleDownloadImages(order._id)}
                                      disabled={downloadingImages === order._id}
                                    >
                                      <span className="purchase_btn_icon">
                                        {downloadingImages === order._id ? (
                                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M12 15.75L8.25 12L9.66 10.59L11.25 12.18V3H12.75V12.18L14.34 10.59L15.75 12L12 15.75ZM5.25 21C4.85 21 4.5 20.85 4.2 20.55C3.9 20.25 3.75 19.9 3.75 19.5V15H5.25V19.5H18.75V15H20.25V19.5C20.25 19.9 20.1 20.25 19.8 20.55C19.5 20.85 19.15 21 18.75 21H5.25Z"
                                              fill="currentColor"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                      <span>{downloadingImages === order._id ? 'Downloading...' : 'Download'}</span>
                                    </button>

                                    <div className="invoice_link">
                                      <button
                                        className="purchase_invoice_btn"
                                        onClick={() => handleDownloadInvoice(order._id, order.orderNumber)}
                                        disabled={downloadingInvoice === order._id}
                                      >
                                        {downloadingInvoice === order._id ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Downloading...
                                          </>
                                        ) : (
                                          'Download Invoice'
                                        )}
                                      </button>

                                      <button
                                        className="purchase_invoice_btn_sec"
                                        onClick={() => handleDownloadInvoice(order._id, order.orderNumber)}
                                        disabled={downloadingInvoice === order._id}
                                      >
                                        {downloadingInvoice === order._id ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Downloading...
                                          </>
                                        ) : (
                                          'Invoice'
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>


                                <div className="purchase_actions purchase_action_mid">
                                  <button
                                    className="purchase_download_btn purchase_download_btn_mid"
                                    onClick={() => handleDownloadImages(order._id)}
                                    disabled={downloadingImages === order._id}
                                  >
                                    <span className="purchase_btn_icon">
                                      {downloadingImages === order._id ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      ) : (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                        >
                                          <path
                                            d="M12 15.75L8.25 12L9.66 10.59L11.25 12.18V3H12.75V12.18L14.34 10.59L15.75 12L12 15.75ZM5.25 21C4.85 21 4.5 20.85 4.2 20.55C3.9 20.25 3.75 19.9 3.75 19.5V15H5.25V19.5H18.75V15H20.25V19.5C20.25 19.9 20.1 20.25 19.8 20.55C19.5 20.85 19.15 21 18.75 21H5.25Z"
                                            fill="currentColor"
                                          />
                                        </svg>
                                      )}
                                    </span>
                                    <span>{downloadingImages === order._id ? 'Downloading...' : 'Download'}</span>
                                  </button>

                                  <div className="invoice_link invoice_link_btn">
                                    <button
                                      className="purchase_invoice_btn"
                                      onClick={() => handleDownloadInvoice(order._id, order.orderNumber)}
                                      disabled={downloadingInvoice === order._id}
                                    >
                                      {downloadingInvoice === order._id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                          Downloading...
                                        </>
                                      ) : (
                                        'Download Invoice'
                                      )}
                                    </button>

                                    <button
                                      className="purchase_invoice_btn_sec"
                                      onClick={() => handleDownloadInvoice(order._id, order.orderNumber)}
                                      disabled={downloadingInvoice === order._id}
                                    >
                                      {downloadingInvoice === order._id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                          Downloading...
                                        </>
                                      ) : (
                                        'Invoice'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PreviousPurchase;
